import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Mail, KeyRound, ShieldCheck, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPANY_LINES } from "@/lib/company";
import { LOGO } from "@/components/Layout";
import { IsoBadge } from "@/components/IsoBadge";
import { inputClass } from "@/components/FieldInput";
import { VideoGuideLink } from "@/components/VideoGuideLink";

export default function Login() {
  const { user, setUser, setAupNeeded } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [cid, setCid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [totp, setTotp] = useState("");

  if (user) return <Navigate to="/" replace />;

  const request = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { email });
      setCid(data.challenge_id);
      setDevCode(data.dev_code || null);
      setStep(2);
      setCode("");
      toast.success(data.message);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { challenge_id: cid, code });
      if (data.mfa_required) {
        setMfa(data);
        setTotp("");
        setStep(3);
      } else {
        setAupNeeded(!data.user.aup_accepted_at);
        setUser(data.user);
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const verifyTotp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/verify", { mfa_token: mfa.mfa_token, code: totp });
      if (mfa.enroll) toast.success("App autenticatore registrata");
      setAupNeeded(!data.user.aup_accepted_at);
      setUser(data.user);
    } catch (err) {
      toast.error(errMsg(err));
      if (err.response?.data?.detail?.includes("scaduta")) setStep(1);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#F8FAFC]">
      <section className="hidden lg:flex flex-col justify-between bg-[#0B132B] text-slate-300 p-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#38BDF8 1px, transparent 1px), linear-gradient(90deg, #38BDF8 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative flex items-center gap-3">
          <img src={LOGO} alt="Trust Master" className="h-11 w-11 rounded-lg bg-white p-1 object-contain" />
          <span className="font-heading text-white text-xl font-semibold">Trust Master CRM</span>
        </div>
        <div className="relative max-w-lg">
          <p className="text-xs uppercase tracking-[0.22em] text-sky-400 mb-5">Piattaforma professionale per la gestione dei Trust</p>
          <h1 className="font-heading text-5xl font-bold text-white leading-[1.08] tracking-tight">Riservatezza patrimoniale, governata con metodo.</h1>
          <div className="mt-10 space-y-4 text-sm">
            {[[ShieldCheck, "Sistema di gestione conforme a ISO/IEC 27001:2022"], [KeyRound, "Accesso senza password con codice monouso (OTP)"], [Lock, "Backup cifrati AES-256 e registro eventi a prova di manomissione"]].map(([I, t]) => (
              <div key={t} className="flex items-center gap-3"><I className="h-4 w-4 text-sky-400" />{t}</div>
            ))}
          </div>
        </div>
        <div className="relative flex items-end justify-between gap-6">
          <IsoBadge testId="login-iso-badge" />
          <div className="text-[11px] leading-relaxed text-slate-500 text-right max-w-xl" data-testid="login-company-footer">
            {COMPANY_LINES.map((l) => <p key={l}>{l}</p>)}
          </div>
        </div>
      </section>

      <section className="flex items-center justify-center p-6">
        <div className="w-full max-w-md tm-fade">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src={LOGO} alt="Trust Master" className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-heading text-lg font-semibold">Trust Master CRM</span>
          </div>
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].slice(0, mfa ? 3 : 2).map((s) => <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${step >= s ? "bg-[#0A2540]" : "bg-slate-200"}`} />)}
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-2">Passo {step} di {mfa ? 3 : 2}</p>
          <h2 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">{step === 1 ? "Accedi" : step === 2 ? "Verifica il codice" : mfa?.enroll ? "Registra l'app autenticatore" : "Codice dell'app"}</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">
            {step === 1 ? "Inserisci la tua email aziendale: ti invieremo un codice monouso di 6 cifre."
              : step === 2 ? <>Abbiamo inviato un codice a <b className="text-slate-800">{email}</b>. Scade tra 10 minuti.</>
              : mfa?.enroll ? "Il tuo profilo richiede un secondo fattore (TM-ISMS-06). Inquadra il QR con Google Authenticator, Microsoft Authenticator o un'app equivalente, poi inserisci il codice a 6 cifre."
              : "Apri l'app autenticatore sul telefono e inserisci il codice a 6 cifre di Trust Master CRM."}
          </p>

          {step === 3 ? (
            <form onSubmit={verifyTotp} className="space-y-4">
              {mfa?.enroll && (
                <div className="flex flex-col items-center gap-3 border border-slate-200 rounded-xl p-4 bg-white" data-testid="totp-enroll-box">
                  <img src={mfa.qr} alt="QR code app autenticatore" className="h-44 w-44" data-testid="totp-qr" />
                  <p className="text-xs text-slate-500">Oppure inserisci manualmente la chiave:</p>
                  <code className="font-mono text-xs break-all text-center bg-slate-50 px-2 py-1 rounded" data-testid="totp-secret">{mfa.secret}</code>
                </div>
              )}
              <input data-testid="login-totp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required autoFocus
                className="w-full h-16 text-center text-3xl font-mono font-bold tracking-[0.6em] rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
              <button data-testid="login-verify-totp-button" disabled={busy || totp.length !== 6} className="w-full h-12 rounded-lg bg-[#0A2540] text-white font-semibold hover:bg-[#031B33] transition-colors disabled:opacity-60">
                {busy ? "Verifica…" : mfa?.enroll ? "Conferma registrazione e accedi" : "Verifica e accedi"}
              </button>
              <p className="text-xs text-slate-400">Hai perso il telefono? Chiedi all'Amministratore di azzerare la registrazione dell'app.</p>
            </form>
          ) : step === 1 ? (
            <form onSubmit={request} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input data-testid="login-email-input" type="email" required autoFocus className={`${inputClass} h-12 pl-10`} placeholder="nome@trustmaster.it" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button data-testid="login-request-otp-button" disabled={busy} className="w-full h-12 rounded-lg bg-[#0A2540] text-white font-semibold hover:bg-[#031B33] transition-colors disabled:opacity-60">
                {busy ? "Invio in corso…" : "Invia codice di accesso"}
              </button>
              <VideoGuideLink />
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <input data-testid="login-otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required autoFocus
                className="w-full h-16 text-center text-3xl font-mono font-bold tracking-[0.6em] rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
              {devCode && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="login-dev-code">Ambiente di anteprima — codice: <b className="font-mono">{devCode}</b></p>}
              <button data-testid="login-verify-otp-button" disabled={busy || code.length !== 6} className="w-full h-12 rounded-lg bg-[#0A2540] text-white font-semibold hover:bg-[#031B33] transition-colors disabled:opacity-60">
                {busy ? "Verifica…" : "Verifica e accedi"}
              </button>
              <div className="flex justify-between text-sm">
                <button type="button" data-testid="login-back-button" onClick={() => setStep(1)} className="flex items-center gap-1 text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" />Cambia email</button>
                <button type="button" data-testid="login-resend-button" onClick={request} className="text-blue-600 hover:text-blue-800">Invia nuovo codice</button>
              </div>
            </form>
          )}
          <p className="text-xs text-slate-400 mt-10 leading-relaxed">Dopo 5 codici errati consecutivi l'utenza viene bloccata e può essere sbloccata solo dal Gestore Utenti. Tutti i tentativi di accesso sono registrati con data, ora e indirizzo IP.</p>
          <div className="flex gap-4 mt-6 text-xs">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="login-privacy-link">Informativa privacy</Link>
            <Link to="/cookie" className="text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="login-cookie-link">Cookie policy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
